import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Lock, FileQuestion, LogIn } from "lucide-react";
import { setPageMeta, setOpenGraph, setJsonLd, setRobots, demoteHeadings, addImageAlts, cleanHtml } from "../seo";
import { customPageMeta, fixTitle } from "../pageMeta";
import { getPrerender } from "../prerender";
import { SiteHeader } from "../components/site/SiteHeader";
import { SiteFooter } from "../components/site/SiteFooter";
import { useSiteNav, visibleSectionKeys } from "../components/site/useSiteNav";
import api from "../api";

// URL pubblici dismessi: redirect (equivalente a 301 lato client) verso la destinazione esterna.
const REDIRECTS = { "https-www-piusostenibili-it": "https://piusostenibili.it/" };
// Slug rimossi definitivamente: trattati come "pagina rimossa" (gone) con noindex.
const GONE_SLUGS = new Set(["indicizzazione"]);
// Slug corretti (vecchio -> nuovo canonico): redirect interno equivalente a 301.
const SLUG_REDIRECTS = {
  "smatworking": "smart-working",
  "sostenibilit": "sostenibilita",
  "jobtimehr-accademy": "jobtimehr-academy",
};
const JSONLD_IDS = ["ld-breadcrumb", "ld-webpage", "ld-faq"];

const PageShell = ({ children }) => {
  const nav = useSiteNav();
  return (
    <div className="min-h-screen bg-[#0d1b2f] text-white">
      <SiteHeader pages={nav.pages} sections={visibleSectionKeys(nav.layout)} />
      <main className="max-w-4xl mx-auto px-6 py-12">{children}</main>
      <SiteFooter pages={nav.pages} />
    </div>
  );
};

const Breadcrumb = ({ page }) => (
  <nav aria-label="Breadcrumb" className="mb-6 text-[13px] font-semibold text-gray-400" data-testid="custom-page-breadcrumb">
    <Link to="/" className="hover:text-[#ff7e00] transition-colors">Home</Link>
    {" "}<span aria-hidden="true" className="mx-1.5 text-gray-600">›</span>{" "}
    <span aria-current="page" className="text-gray-200">{fixTitle(page.title)}</span>
  </nav>
);

const initialState = (slug) => {
  if (GONE_SLUGS.has(slug)) return { status: "gone", page: null, slug };
  const page = getPrerender(`/p/${slug}`)?.page;
  return page ? { status: "ok", page, slug } : { status: "loading", page: null, slug };
};

const CustomPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [state, setState] = useState(() => initialState(slug));

  useEffect(() => {
    // Redirect 301-equivalente verso URL esterni per i vecchi slug dismessi.
    if (REDIRECTS[slug]) {
      window.location.replace(REDIRECTS[slug]);
      return;
    }
    // Redirect interno 301-equivalente verso lo slug corretto (canonico).
    if (SLUG_REDIRECTS[slug]) {
      navigate(`/p/${SLUG_REDIRECTS[slug]}`, { replace: true });
      return;
    }
    let alive = true;
    if (GONE_SLUGS.has(slug)) { setState({ status: "gone", page: null, slug }); return; }
    // Contenuto pre-renderizzato già visibile: aggiornamento silenzioso senza "Caricamento…".
    setState((s) => (s.slug === slug && s.status === "ok" ? s : { status: "loading", page: null, slug }));
    api.get(`/pages/${slug}`)
      .then((r) => { if (alive) setState({ status: "ok", page: r.data.page, slug }); })
      .catch((e) => {
        if (!alive) return;
        const code = e?.response?.status;
        // Errore di rete/server: se il contenuto pre-renderizzato è già visibile lo si mantiene.
        if (!code || code >= 500) { setState((s) => (s.slug === slug && s.status === "ok" ? s : { status: "notfound", page: null, slug })); return; }
        const status = code === 403 ? "forbidden" : code === 410 ? "gone" : "notfound";
        setState({ status, page: null, slug });
      });
    return () => { alive = false; };
  }, [slug, navigate]);

  // Deindicizzazione: pagine rimosse/non trovate o marcate noindex nel CMS.
  useEffect(() => {
    const bad = ["notfound", "gone"].includes(state.status);
    setRobots(bad ? "noindex, nofollow" : state.page?.noindex ? "noindex, follow" : null);
    return () => setRobots(null);
  }, [state.status, state.page]);

  useEffect(() => {
    if (state.status !== "ok" || !state.page) return;
    const m = customPageMeta(state.page, slug);
    setPageMeta({ title: m.title, description: m.description, canonical: m.canonical });
    setOpenGraph({ title: m.title, description: m.description, image: m.image, url: m.canonical, type: "website" });
    JSONLD_IDS.forEach((id) => setJsonLd(id, m.jsonLd[id]));
    return () => JSONLD_IDS.forEach((id) => setJsonLd(id, null));
  }, [state.status, state.page, slug]);

  if (REDIRECTS[slug]) {
    return <PageShell><div className="text-gray-400" data-testid="custom-page-redirect">Reindirizzamento in corso…</div></PageShell>;
  }

  if (state.status === "loading") {
    return <PageShell><div className="text-gray-400" data-testid="custom-page-loading">Caricamento…</div></PageShell>;
  }

  if (state.status === "forbidden") {
    return (
      <PageShell>
        <div className="text-center py-16" data-testid="custom-page-forbidden">
          <div className="mx-auto w-16 h-16 rounded-2xl bg-[#ff7e00]/12 border border-[#ff7e00]/40 flex items-center justify-center mb-6"><Lock size={28} className="text-[#ff7e00]" /></div>
          <h1 className="text-3xl font-black">Pagina riservata</h1>
          <p className="mt-3 text-gray-400">Questa pagina è visibile solo agli utenti registrati.</p>
          <Link to="/login" data-testid="custom-page-login"
            className="mt-8 inline-flex items-center gap-2 rounded-full bg-[#ff7e00] hover:bg-[#e56f00] text-white px-5 py-2.5 text-[14px] font-bold transition-colors">
            <LogIn size={16} /> Accedi
          </Link>
        </div>
      </PageShell>
    );
  }

  if (state.status === "notfound" || state.status === "gone") {
    const gone = state.status === "gone";
    return (
      <PageShell>
        <div className="text-center py-16" data-testid={gone ? "custom-page-gone" : "custom-page-notfound"}>
          <div className="mx-auto w-16 h-16 rounded-2xl bg-white/5 border border-white/10 flex items-center justify-center mb-6"><FileQuestion size={28} className="text-gray-400" /></div>
          <h1 className="text-3xl font-black">{gone ? "Pagina rimossa" : "Pagina non trovata"}</h1>
          <p className="mt-3 text-gray-400">{gone ? "Questa pagina è stata rimossa e non è più disponibile." : "La pagina richiesta non esiste o non è più disponibile."}</p>
        </div>
      </PageShell>
    );
  }

  const page = state.page;
  const title = fixTitle(page.title);
  return (
    <PageShell>
      <Breadcrumb page={page} />
      <h1 className="text-4xl sm:text-5xl font-black tracking-tight" data-testid="custom-page-title">{title}</h1>
      <div
        className="custom-page-content mt-8 text-gray-200"
        data-testid="custom-page-content"
        dangerouslySetInnerHTML={{ __html: addImageAlts(demoteHeadings(cleanHtml(page.content)), title) }}
      />
    </PageShell>
  );
};

export default CustomPage;
